import React, { useState, useEffect } from 'react';
import { capitalizeWords } from '../utils/format';
import { useNavigate } from 'react-router-dom';
import { candidateAPI } from '../services/api';
import Navigation from '../components/Navigation';
import './Dashboard.css';
import PlusIcon from '../components/PlusIcon';

function Dashboard({ onLogout, onFeedback }) {
  const [stats, setStats] = useState({
    in_progress: 0,
    recruited: 0,
    rejected: 0,
    declined: 0,
    archived: 0,
  });
  const [recentCandidates, setRecentCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();


  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Counts come from the server, which counts in SQL over every candidate this
      // user can access. Deriving them from the list below would cap them at its
      // 100-row limit and report zero recruited (they're archived, so the default
      // list excludes them).
      const [counts, candidates] = await Promise.all([
        candidateAPI.stats(),
        candidateAPI.list(),
      ]);

      setStats(counts);
      setRecentCandidates(candidates.slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Navigation onLogout={onLogout} onFeedback={onFeedback} />
        <div className="container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navigation onLogout={onLogout} onFeedback={onFeedback} />
      <div className="container">
        <h1>Dashboard</h1>

        {/* Each card is a shortcut into the matching filtered list. */}
        <div className="stats-grid">
          <button
            type="button"
            className="stat-card"
            onClick={() => navigate('/candidates')}
          >
            <h3>In Progress</h3>
            <p className="stat-number next-glow-text">{stats.in_progress}</p>
          </button>
          <button
            type="button"
            className="stat-card"
            onClick={() => navigate('/candidates?status=rejected')}
          >
            <h3>Rejected</h3>
            <p className="stat-number is-rejected next-glow-text">{stats.rejected}</p>
          </button>
          <button
            type="button"
            className="stat-card"
            onClick={() => navigate('/candidates?status=offer_declined')}
          >
            <h3>Offer Declined</h3>
            <p className="stat-number is-declined next-glow-text">{stats.declined}</p>
          </button>
          <button
            type="button"
            className="stat-card"
            onClick={() => navigate('/candidates?status=recruited')}
          >
            <h3>Recruited</h3>
            <p className="stat-number is-recruited next-glow-text">{stats.recruited}</p>
          </button>
        </div>

        <div className="section">
          <h2>Recent Candidates</h2>
          {recentCandidates.length > 0 ? (
            <table className="candidate-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Position</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentCandidates.map((candidate) => (
                  <tr key={candidate.id}>
                    <td>{`${candidate.first_name} ${candidate.last_name}`}</td>
                    <td>{candidate.email}</td>
                    <td>{capitalizeWords(candidate.position_applied)}</td>
                    <td>
                      <span className={`status-badge status-${candidate.status}`}>
                        {candidate.status === 'offer_sent' ? 'job offer sent' : candidate.status === 'candidate_signed' ? 'candidate signed contract' : candidate.status === 'recruited' ? 'Recruited !' : candidate.status === 'background_check' ? 'background checked' : candidate.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => navigate(`/candidates/${candidate.id}`)}
                        className="btn-small btn-view next-glow next-glow-soft"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No candidates yet.</p>
          )}
        </div>

        <div className="actions">
          <button
            onClick={() => navigate('/candidates/new')}
            className="btn-primary btn-add-candidate next-glow next-glow-soft"
          >
            <PlusIcon />
            Add New Candidate
          </button>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
